import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "owners-engineering",
  kind: "horizontal",
  title: "Owner's Engineering & Technical Advisory",
  shortTitle: "Owner's Engineering",
  status: "draft",
  tier: "experience",
  seoTitle: "Owner's Engineering & Technical Advisory | GA",
  seoDescription:
    "Independent technical advisory, vendor bid evaluation, controls specification review, and commissioning oversight for plant owners and capital projects.",
  summary:
    "Independent owner representation during major retrofits and migrations: vendor proposal evaluation, equipment specifications, factory acceptance witnessing, and commissioning oversight.",
  image: "/images/industries/energy-hero.webp",
  relatedCapabilitySlugs: ["automation-operations"],
  relatedIndustrySlugs: ["energy"],
  relatedPostSlugs: [
    "no-happy-path",
    "s1000d-conversion-failure-modes",
    "hot-cutover-realities",
    "auditing-the-undocumented",
  ],
  engineering: [
    "Independent owner representation across utility, generation, and critical industrial capital projects",
    "Technical specification authoring, RFP requirements development, and preliminary engineering design packages",
    "Multi-vendor proposal and bid evaluation across controls hardware, DCS, software platforms, and field services",
    "P&ID, control logic diagram, I/O list, electrical schematic, and vendor submittal reviews",
    "Factory Acceptance Testing (FAT) witnessing, test protocol verification, and vendor punch-list resolution",
    "On-site commissioning oversight, outage cutover verification, and contractor milestone sign-offs",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Protecting the Owner's Operational and Financial Interests",
    },
    {
      type: "paragraph",
      text: "Major capital retrofits and control-system migrations frequently stumble when equipment vendors and prime contractors optimize for their own delivery schedules rather than the plant's long-term operating realities. Without seasoned technical advocates representing the asset owner, critical interface assumptions slip through unverified, change orders proliferate, and commissioning discoveries force expensive emergency workarounds.",
    },
    {
      type: "heading",
      level: 2,
      text: "Independent Technical Rigor from RFP to Commercial Operation",
    },
    {
      type: "paragraph",
      text: "Owner's engineering provides an uncompromised technical check across every delivery milestone. By evaluating vendor proposals against actual operational constraints, scrutinizing I/O assignments and control logic diagrams before hardware fabrication, and witnessing factory acceptance testing with unyielding scrutiny, the owner enters outage windows with verified systems and predictable risk.",
    },
  ],
  phases: {
    study: {
      focus:
        "Define project technical scope, existing plant baseline conditions, regulatory constraints, and owner performance requirements.",
      produces: [
        "Preliminary project technical scope and baseline plant condition assessment",
        "RFP technical specification package and evaluation criteria matrix",
        "Vendor pre-qualification standards and interface boundary definitions",
        "Owner risk assessment and schedule constraint analysis",
      ],
      fit: {
        good: [
          "Plant owners, utilities, and industrial asset operators undertaking capital retrofits, DCS migrations, or facility modernizations",
          "Organizations needing independent technical oversight to evaluate vendor proposals, witness testing, and verify contractor deliverables",
        ],
        poor: [
          "Turnkey engineering-procurement-construction (EPC) contractors seeking sub-tier design drafting rather than owner representation",
          "Commodity procurement engagements where technical specifications and vendor performance criteria carry no operational consequence",
        ],
      },
    },
    "initial-design": {
      focus:
        "Manage competitive RFP processes, evaluate technical vendor proposals, conduct bid leveling, and establish vendor contracts.",
      produces: [
        "Comprehensive vendor proposal evaluation and technical scoring reports",
        "Bid-leveling comparison across hardware, software, licensing, and field engineering",
        "Recommended vendor selection justifications and commercial risk findings",
        "Contract technical appendices and contractor milestone payment criteria",
      ],
    },
    "detailed-design": {
      focus:
        "Review detailed contractor engineering deliverables, P&IDs, control logic, I/O lists, and factory acceptance test (FAT) protocols.",
      produces: [
        "Formal engineering review comments on vendor drawings, P&IDs, and software specifications",
        "I/O point assignment, loop diagram, and instrument schedule reconciliation reports",
        "Factory Acceptance Test (FAT) witness protocols, test matrices, and punch-list tracking",
        "Outage cutover schedule review and critical path risk mitigation plans",
      ],
    },
    implementation: {
      focus:
        "Witness factory acceptance tests, oversee on-site installation and loop checks, supervise commissioning, and verify final documentation.",
      produces: [
        "FAT witnessing reports, deficiency logs, and corrective action sign-offs",
        "On-site commissioning oversight logs and loop check verification records",
        "Unit startup observation reports and contractor punch-list closure tracking",
        "Final as-built drawing verification and asset turnover documentation packages",
      ],
    },
  },
};
